/* The filter panel's SHELL — the popup frame, its head, its tab strip, its body box and its footer.
 *
 * Transcribed from KANON `Programy v2 - D3 (0.8)` and unchanged since; the exercise library's
 * `/cwiczenia` panel is the second surface to draw it, byte for byte, because its own specimen
 * transcribes the Programy panel verbatim. On the second consumer the block moves here — the house
 * rule, and the one this file exists to satisfy.
 *
 * Read off the rendered specimen at 1512x1000 with the panel open:
 *   scrim    rgba(0,0,0,.44) dark / rgba(17,17,19,.22) light, `fpFade .18s ease`
 *   panel    342.5 wide, radius 13, --bg-card over a --border-2 hairline, 0 19px 48px,
 *            `fpIn .2s cubic-bezier(.2,.8,.3,1)`, transform-origin top right
 *   head     padding 13 / 14.5 / 9.5, gap 8; title 13.5 / 800 / --ls-section
 *   close    22.5 square, radius 7, --bg-inset, --text-2, 9.5 / 700
 *   tabs     padding 0 14.5 9.5, gap 5, a --border rule under them
 *   tab      25.5 tall, 0 10.5 across, radius 8, 10 / 700; selected --text over --on-ink
 *   count    9 / 700, tabular; --text-3 on a tab, or --on-ink-soft inside a selected one
 *   body     padding 13 / 14.5 / 14.5, gap 9
 *   section  8 / 700 / --ls-eyebrow, uppercase; --text-2, NOT the handoff's --text-3 (see below)
 *   foot     padding 10.5 14.5, gap 8, --bg-inset over a --border rule
 *   clear    10 / 700 / --text-2 . apply 30.5 tall, 0 13, radius 9.5, --bg-card, --shadow-card
 *
 * **Two runs read `--text-2` where the handoff writes `--text-3`**: the section title and the
 * figure inside `Pokaz N`, both measured at 3.01:1 in Flame against the 4.5:1 a readable run owes.
 * Owner decision of 17 September 2026, taken on the exercise library and recorded in
 * `docs/design/kanon-cwiczenia-2026-09-17/README.md` (entry 18). This stylesheet is SHARED, so the
 * plan library's own filter panel takes the same two steps - which is the point rather than a side
 * effect: the same word at the same size on the same surface should not be legible on one screen
 * and not on the next.
 *
 * **`Pokaz N` is NEUTRAL, not ember.** The specimen writes `background: T.card` with a `T.line2`
 * hairline and `T.shadow` - a card-coloured button with a neutral elevation. It is not the screen's
 * primary action (the ember square in the header is), it does not apply anything (the facets are
 * already applied), and it closes a panel. Reproduced as authored.
 *
 * ── What this file does NOT own: the BODIES ─────────────────────────────────────────────────
 * A tab's contents are the screen's own, and the two screens' contents are genuinely different
 * controls rather than one block drawn twice: Programy has category chips carrying `--cat-ink`, a
 * mode radio list, a day slider and a client list; Cwiczenia has type chips carrying `--type-ink`,
 * a muscle checklist in four regions, a grid of equipment photographs and a source radio. They
 * share a shape and not a declaration block, and pushing them together would mean one stylesheet
 * reading two colour vocabularies through a third name nobody uses.
 *
 * `.control` — the off-screen native input every chip, radio and checkbox wraps — is deliberately NOT
 * here: a `:has(.control:focus-visible)` selector written in a consumer's own stylesheet would look
 * for that consumer's hashed class name and never match this file's, so the focus ring would
 * silently stop being drawn. It is four lines and it lives beside the controls that read it.
 *
 * **Nothing is declared on both sides.** A property stated here AND in a consumer's stylesheet is
 * settled by chunk order, which differs between the dev server and the build; this repository has
 * paid for that once. Every consumer states its own layout and its own bodies, and neither restates
 * a shell property.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* Above everything the page can raise, and it has to sit on the POSITIONER rather than on the popup:
 * `z-index` applies to positioned elements only, Base UI positions through inline styles on the
 * positioner, and the popup inside it is `position: static`. `MenuSurface` carries the same note.
 *
 * Measured on `/cwiczenia`, which is the first list under this panel with a STICKY column band: the
 * band is `z-index: 1` inside the page's own stacking context and it painted straight over the
 * popup's tab strip and its section title, half-covering a control that was still clickable. 40 is
 * the app's popup layer — the same number `MenuSurface` and `HintPopover` use, above the day dock's
 * 20 and below the confirm dialog's 30/31, which is modal and should cover this. */
.positioner {
  z-index: 40;
}

.scrim {
  position: fixed;
  inset: var(--space-0);
  background: var(--scrim-fp);
  animation: fp-fade var(--dur-fp-fade) var(--ease-in-out) backwards;
}

.panel {
  display: flex;
  flex-direction: column;
  width: var(--w-pl-panel);
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-13px);
  box-shadow: var(--shadow-fp);

  /* The canon's own `fpIn`, renamed to this repository's kebab-case keyframe convention. Opacity
   * and transform only, so nothing lays out again mid-flight; the origin is the specimen's
   * `top right`. `prefers-reduced-motion` is answered once, in `tokens/base.css`, and never per
   * component.
   *
   * `backwards`, not the specimen's `both`, and the difference is invisible: both keyframe sets end
   * at the element's own natural state (opacity 1, no transform), so `forwards` fills nothing.
   * What it costs is real — a `both` animation stays in `document.getAnimations()` for the rest of
   * the element's life, Base UI waits on those before it unmounts a closed popup, and the scrim was
   * measured still covering the scope switch one action after Escape. */
  transform-origin: top right;
  animation: fp-in var(--dur-fp-in) var(--ease-fp) backwards;
}

@keyframes fp-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fp-in {
  from {
    transform: translateY(-8px) scale(0.985);
    opacity: 0;
  }

  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* ── The head ──────────────────────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-13px) var(--space-14-5px) var(--space-9-5px);
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-pl-panel-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-section);
  color: var(--text);
}

.spacer {
  flex: 1;
}

.close {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--h-pl-close);
  height: var(--h-pl-close);
  padding: var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: var(--bg-inset);
  border: none;
  border-radius: var(--radius-7px);
  cursor: pointer;
}

.close:hover {
  color: var(--text);
}

.close:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── The tabs ──────────────────────────────────────────────────────────────────────────────── */

.tabs {
  display: flex;
  gap: var(--space-5px);
  padding: var(--space-0) var(--space-14-5px) var(--space-9-5px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.tab {
  display: inline-flex;
  flex: none;
  gap: var(--space-5px);
  align-items: center;
  height: var(--h-pl-segment);
  padding: var(--space-0) var(--space-10-5px);
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-inset);
  border: none;
  border-radius: var(--radius-8px);
  cursor: pointer;
}

.tabOn {
  color: var(--on-ink);
  background: var(--text);
}

.tab:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.tabCount {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.tabOn .tabCount {
  color: var(--on-ink-soft);
}

/* ── The bodies ────────────────────────────────────────────────────────────────────────────── */

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-13px) var(--space-14-5px) var(--space-14-5px);
}

.sectionTitle {
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-eyebrow);

  /* `--text-2`, not `--text-3`: measured at 3.01:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
  text-transform: uppercase;
}

/* ── Typ ───────────────────────────────────────────────────────────────────────────────────── */

/* ── The footer ────────────────────────────────────────────────────────────────────────────── */

.foot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-10-5px) var(--space-14-5px);
  background: var(--bg-inset);
  border-top: var(--border-hairline) solid var(--border);
}

.clear {
  flex: none;
  padding: var(--space-0);
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: transparent;
  border: none;
  cursor: pointer;
}

.clear:hover {
  color: var(--text);
}

.clear:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* Neutral, and that is the canon's own reading — see the header. */
.apply {
  display: inline-flex;
  flex: none;
  gap: var(--space-6-5px);
  align-items: center;
  height: var(--h-pl-apply);
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-pl-readout);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-9-5px);
  box-shadow: var(--shadow-card);
  cursor: pointer;
}

.apply:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.applyCount {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);

  /* `--text-2`, not `--text-3`: measured at 3.01:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}
